import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import './OptInPage.css';

import { useAuth, parseJwt } from '../auth';
import { getBackendURL } from '../utils';

const OptInPage = () => {
    const { user, setUser } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [trainerName, setTrainerName] = useState('');
    const [phone, setPhone] = useState('');
    const [consent, setConsent] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [googleToken, setGoogleToken] = useState(null);
    // const backendURL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:19027';

    const token = new URLSearchParams(location.search).get('token');

    const fetchOptInDetails = async () => {
        if (!token) return;
        setLoading(true);
        try {
            const response = await fetch(`${getBackendURL()}/api/opt-in-details`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token }),
            });
            if (!response.ok) throw new Error('Failed to fetch opt-in details');
            const data = await response.json();
            setTrainerName(data.trainer_name);
            // Add any additional client details if needed
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOptInDetails();
    }, [token]);

    const handleGoogleSuccess = (response) => {
        console.log('Google login successful:', response);
        setGoogleToken(response.credential);
        setError('');
    };

    const handleGoogleFailure = () => {
        setError('Failed to link Google account. Please try again.');
    };

    const handleSubmitClick = async () => {
        if (!phone) {
            setError('Please enter a valid phone number.');
            return;
        }
        if (!consent) {
            setError('Please consent to receive SMS reminders.');
            return;
        }
        if (!googleToken) {
            setError('Please link your Google account.');
            return;
        }
        try {
            const response = await fetch(`${getBackendURL()}/sms-opt-in`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ phoneNumber: phone, 
                    consentCheckbox: consent, 
                    token, 
                    googleToken }),
            });
            if (!response.ok) throw new Error('Failed to submit opt-in');
            const data = await response.json();
            localStorage.setItem('ptc_access_token', data.access_token);




            const payload = parseJwt(data.access_token);
            const userObj = {
                ...data.user, // Use the user object from the backend response
                email: payload && payload.sub ? payload.sub : undefined,
                role: payload && payload.role ? payload.role : undefined,
                user_id: data.user_id ?? (payload && payload.user_id ? payload.user_id : undefined)
            };
            delete userObj.hashed_password; //Ensure sensitive fields that wont be here anyway are not stored just in case
            // setUser(userObj);
            window.localStorage.setItem('ptc_user', JSON.stringify(userObj));
            // TODO consider using auth js login instead and pass google auth token directly there.


            navigate('/opt-in/confirmed', { state: { trainerName } });
        } catch (err) {
            setError(err.message);
        }
    };

    if (loading) return <div className="text-white text-center p-8">Loading...</div>;
    if (error && !trainerName) return <div className="text-red-500 text-center p-8">Error: {error}</div>;


    if (user?.role?.includes('trainer')) {
        return (
            <div style={{background: 'rgba(0,0,0,0.4', 
                color: 'white', 
                padding: '2rem',
                margin: '2rem',
                fontSize: '3rem'}} className="text-red-500 text-center p-8">
                You cannot access this page as a trainer. Please log in as a client.
            </div>
        );
    }

    return (
        <div className="optin-card">
            <h2>Client Opt-In for SMS Reminders</h2>
            {error && <p className="error">{error}</p>}

            <p>
                This page is for clients to sign up for SMS texts.
            </p>
            <div>
                {trainerName ? (
                    <p>
                        You were invited by a personal trainer, "<strong>{trainerName}</strong>", to receive SMS messages and join this site.
                    </p>
                ) : (
                    <p>
                        No invitation from a trainer was found, but you can still opt in to receive SMS reminders and create an account.
                    </p>
                )}
                    
                    <label>
                        What phone number would you like to receive SMS reminders on?
                       
                <div style={{margin: '2rem 0rem'}}> 
                    <PhoneInput
                            defaultCountry="us"
                            value={phone}
                            onChange={(phone) => setPhone(phone)}
                        />
                </div>
                    </label>

                <label>
                    <input
                        type="checkbox"
                        checked={consent}
                        onChange={(e) => setConsent(e.target.checked)}
                    />
                    I agree to receive ongoing SMS reminders from
                     my trainer via The Personal Trainers Club and from the site itself.
                    These may include appointment reminders or check-ins.
                    It may also include system messages from this site 
                    about your account.
                     Message & data rates may apply.
                      Reply STOP to unsubscribe at any time.
                </label>

                <p>
                    By opting in, you agree to our{' '}
                    <a href="/privacy" target="_blank" rel="noopener noreferrer">
                        Privacy Policy
                    </a>{' '}
                    and{' '}
                    <a href="/terms" target="_blank" rel="noopener noreferrer">
                        Terms of Service
                    </a>.
                </p>

                <p>
                    You can also adjust preferences later via your account settings once you've created an account.
                </p>

                <p className="google-prompt">
                    To opt-in, you must create 
                    an account with The Personal Trainers Club
                    using the Google authentication button below.
                </p>

                {!googleToken && (
                <GoogleLogin
                    clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}
                    buttonText="Link Google Account"
                    onSuccess={handleGoogleSuccess}
                    onFailure={handleGoogleFailure}
                    cookiePolicy={'single_host_origin'}
                />)}

                {googleToken && (
                    
                    <p className="success" style={{color: 'lightgreen'}}>
                        Google account linked successfully! Click Submit to finish.
                        </p>
                )}

                <button onClick={handleSubmitClick}>
                    Submit
                </button>
            </div>
        </div>
    );
};

export default OptInPage;
